import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { includes, defaultTo } from 'lodash';
import classnames from 'classnames';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { transformContentForFaq } from 'site-modules/shared/components/faq/utils';
import { Faq } from 'site-modules/shared/components/faq/faq';
import { ArticleCards } from 'site-modules/editorial/components/article-cards/article-cards';
import { Calculators } from 'site-modules/shared/components/editorial/calculators/calculators';
import { CategoryCards } from 'site-modules/shared/components/category-cards/category-cards';
import { ContentList } from 'site-modules/editorial/components/content-list/content-list';
import { ColumnList } from 'site-modules/editorial/components/column-list/column-list';
import { DropdownWidget } from 'site-modules/editorial/components/dropdown-widget/dropdown-widget';
import { ImageLinks } from 'site-modules/shared/components/editorial/image-links/image-links';
import { MarketPlaceList } from 'site-modules/editorial/components/marketplace-list/marketplace-list';
import { ArticleContent } from 'site-modules/editorial/components/article-content/article-content';
import { ArticlePhoto } from 'site-modules/shared/components/editorial/article-photo/article-photo';
import { Video } from 'site-modules/shared/components/video/';
import { InlineArticle } from 'site-modules/editorial/components/inline-article/inline-article';
import { NativeVideoAd } from 'site-modules/shared/components/native-ad/video/video-native-ad';
import { InfographicEntry } from 'site-modules/editorial/components/infographic-entry/infographic-entry';
import { ArticleTable } from 'site-modules/shared/components/editorial/article-table/article-table';
import { AppraiserSellModule } from 'site-modules/shared/components/appraiser-sell/appraiser-sell';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { TrackCarValueModule } from 'site-modules/shared/components/track-car-value/track-car-value-module';
import { ShoppingLink } from 'site-modules/shared/components/shopping-link/shopping-link';
import { ArticleReadyForEv } from 'site-modules/editorial/components/article-ready-for-ev/article-ready-for-ev';
import { ArticleTreehousePromotion } from 'site-modules/editorial/components/article-treehouse-promotion/article-treehouse-promotion';
import { EngineTypeQuiz } from 'site-modules/shared/components/engine-type-quiz/engine-type-quiz';
import { entriesTypes } from './entries-types';

import { pagesWithThreeMrects } from './ad-constants';
import './entries-renderer.scss';

function getAdBlock({
  adNameWired,
  adNameMobile,
  position,
  wiredBreakpoints,
  mobileBreakpoints,
  renderWhenViewable,
  renderOnScroll,
  customSizes,
  noRefresh,
  refreshable,
  wiredOnly,
  mobileOnly,
  wrapperClass,
  verticalOffset,
}) {
  return (
    <NativeAd
      adNameWired={adNameWired}
      adNameMobile={adNameMobile}
      position={position}
      wiredBreakpoints={wiredBreakpoints}
      mobileBreakpoints={mobileBreakpoints}
      nativeStyle="none"
      renderWhenViewable={!!renderWhenViewable}
      verticalOffset={verticalOffset}
      renderOnScroll={!!renderOnScroll}
      dfpCollapse
      customSizes={customSizes}
      noRefresh={noRefresh}
      refreshable={refreshable}
      wiredOnly={wiredOnly}
      mobileOnly={mobileOnly}
      useMinHeight
      showLabel
      wrapperClass={wrapperClass}
    />
  );
}

getAdBlock.defaultProps = {
  adNameWired: 'native',
  adNameMobile: 'mnative',
  renderWhenViewable: false,
  renderOnScroll: false,
  customSizes: [],
  noRefresh: true,
  refreshable: false,
  wiredBreakpoints: {},
  mobileBreakpoints: {},
  wiredOnly: false,
  mobileOnly: false,
  wrapperClass: '',
  verticalOffset: undefined,
};

getAdBlock.propTypes = {
  adNameWired: PropTypes.string,
  adNameMobile: PropTypes.string,
  position: PropTypes.string.isRequired,
  wiredBreakpoints: PropTypes.objectOf(PropTypes.bool),
  mobileBreakpoints: PropTypes.objectOf(PropTypes.bool),
  renderWhenViewable: PropTypes.bool,
  renderOnScroll: PropTypes.bool,
  customSizes: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  noRefresh: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.bool]),
  refreshable: PropTypes.bool,
  mobileOnly: PropTypes.bool,
  wiredOnly: PropTypes.bool,
  wrapperClass: PropTypes.string,
  verticalOffset: PropTypes.string,
};

function RegularContainer({ className, children }) {
  return (
    <Container className={classnames(`${className} p-0`, { 'mb-2': !className.includes('mb-') })}>
      <Row>{children}</Row>
    </Container>
  );
}

RegularContainer.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

RegularContainer.defaultProps = {
  className: '',
  children: null,
};

export function renderEntries({
  entriesToRender,
  withWrapper = false,
  slug = null,
  categories = [],
  options = {},
  pathname,
  includeAppraisalSellModuleInEntries = false,
  includeTreehousePromo,
}) {
  const entriesToRenderLength = entriesToRender.length;
  const isEntriesToRenderLengthGreatThanFour = entriesToRenderLength > 4;
  const entriesToRenderLastIndex = entriesToRenderLength - 1;
  const { isMobile, canonical, contentId, pageTitle } = options;
  const entryPath = defaultTo(options.entryPath, []);
  let hasVideo = false;

  return entriesToRender.reduce((entries, entry, index) => {
    switch (true) {
      case entriesTypes.articleGroup.test(entry.id): {
        entries.push(
          <RegularContainer key={entry.id}>
            <Col xs={12}>
              <ArticleCards entry={entry} />
            </Col>
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.featureGrid.test(entry.id): {
        entries.push(
          <RegularContainer key={entry.id}>
            <CategoryCards entry={entry} />
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.contentList.test(entry.id): {
        entries.push(
          withWrapper ? (
            <RegularContainer key={entry.id}>
              <Col>
                <ContentList entry={entry} />
              </Col>
            </RegularContainer>
          ) : (
            <ContentList entry={entry} key={entry.id} />
          )
        );
        break;
      }
      case entriesTypes.columnList.test(entry.id): {
        entries.push(
          withWrapper ? (
            <RegularContainer key={entry.id}>
              <Col>
                <ColumnList entry={entry} />
              </Col>
            </RegularContainer>
          ) : (
            <ColumnList entry={entry} key={entry.id} />
          )
        );
        break;
      }
      case entriesTypes.calculators.test(entry.id): {
        entries.push(
          withWrapper ? (
            <RegularContainer key={entry.id}>
              <Col>
                <Calculators />
              </Col>
            </RegularContainer>
          ) : (
            <Calculators key={entry.id} />
          )
        );
        break;
      }
      case entriesTypes.edHighImpact.test(entry.id): {
        entries.push(
          <RegularContainer className="high-impact p-0" key={entry.id}>
            <Col>
              <NativeAd
                adNameWired="edhighimpact"
                adNameMobile="medhighimpact"
                nativeStyle="none"
                position={'1'}
                all
                renderWhenViewable={false}
                refreshable={false}
                useMinHeight
              />
            </Col>
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.contentCol.test(entry.id): {
        entries.push(
          <Col xs={12} md={8} key={entry.id} className="pe-md-2">
            {renderEntries({
              entriesToRender: entry.children(),
              withWrapper: false,
              slug: null,
              options,
            })}
          </Col>
        );
        break;
      }
      case entriesTypes.rightRailCol.test(entry.id): {
        const shouldAddThirdMrect = includes(pagesWithThreeMrects, slug);
        const adAboveRightRail = {
          adNameWired: 'mrect',
          position: '1',
          wiredBreakpoints: { md: false, lg: true, xl: true },
          renderWhenViewable: isMobile,
          noRefresh: true,
          wiredOnly: true,
          verticalOffset: '100%',
        };
        const adUnderRightRail = {
          ...adAboveRightRail,
          position: '2',
        };
        const secondaAdUnderRightRail = {
          ...adAboveRightRail,
          position: '3',
          renderOnScroll: true,
        };
        entries.push(
          <Col xs={12} md={4} key={entry.id} className="d-md-flex flex-md-column right-rail-from-entries-renderer">
            <div className="mb-2">{getAdBlock(adAboveRightRail)}</div>
            {renderEntries({
              entriesToRender: entry.children(),
              withWrapper: false,
              slug: null,
              options,
            })}
            {getAdBlock(adUnderRightRail)}
            {shouldAddThirdMrect && (
              <div className="right-rail-third-mrect d-flex justify-content-center align-items-end">
                {getAdBlock(secondaAdUnderRightRail)}
              </div>
            )}
            <NativeVideoAd
              wrapperClass={classnames('native-video-wrapper', {
                separator: !isMobile,
                'px-1': isMobile,
              })}
              wiredBreakpoints={{
                lg: true,
                xl: true,
              }}
              mobileBreakpoints={{
                md: true,
                sm: true,
                xs: true,
              }}
            />
          </Col>
        );
        break;
      }
      case entriesTypes.externalContent.test(entry.id): {
        entries.push(
          <RegularContainer key={entry.id} className="mb-0">
            <Col>
              {renderEntries({
                entriesToRender: entry.children(),
                withWrapper: false,
                slug: null,
                options,
              })}
            </Col>
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.container.test(entry.id): {
        const colsCount = entry.metadata('cols').number();
        entries.push(
          <RegularContainer key={entry.id}>
            {entry.children().map(containerEntry =>
              colsCount && colsCount > 0 ? (
                <Col xs={12} md={12 / colsCount} key={containerEntry.id}>
                  {renderEntries({
                    entriesToRender: [containerEntry],
                    withWrapper: false,
                    slug,
                    options,
                  })}
                </Col>
              ) : (
                renderEntries({
                  entriesToRender: [containerEntry],
                  withWrapper: false,
                  slug,
                  options,
                })
              )
            )}
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.tcoDropdowns.test(entry.id): {
        entries.push(
          <RegularContainer key={entry.id}>
            <Col>
              <DropdownWidget widgetType="tco" />
            </Col>
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.reviewsDropdowns.test(entry.id): {
        entries.push(
          <RegularContainer key={entry.id}>
            <Col>
              <DropdownWidget />
            </Col>
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.imageLinks.test(entry.id): {
        entries.push(
          <RegularContainer key={entry.id}>
            <Col>
              <ImageLinks entry={entry} />
            </Col>
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.html.test(entry.id): {
        const content = entry.legacy ? entry.content : entry.metadata('htmlContent').value();
        if (content && content.length > 0) {
          const key = `${entry.id}-${index}`;
          entries.push(
            <RegularContainer key={key} className={entry.className}>
              <Col className="px-0_5">
                {!!entry.title && <div className="heading-4 mb-1">{entry.title}</div>}
                <ArticleContent content={content} />
              </Col>
            </RegularContainer>
          );
        }
        break;
      }
      case entriesTypes.table.test(entry.id): {
        entries.push(
          <ArticleTable
            className="mb-1"
            key={entry.id}
            shareOptions={{
              contentId,
              canonical,
              entryPath: [...entryPath, entry.id],
              pageTitle,
            }}
            entry={entry}
          />
        );
        break;
      }
      case entriesTypes.marketplaceList.test(entry.id): {
        entries.push(
          <RegularContainer key={entry.id}>
            <Col>
              <MarketPlaceList entry={entry} />
            </Col>
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.faq.test(entry.id): {
        const entryContent = transformContentForFaq(entry);
        entries.push(
          <RegularContainer key={entry.id}>
            <Col>
              <Faq
                {...entryContent}
                generateLearnMoreLinks
                pathname={pathname}
                titleComponent="h2"
                questionComponent="h3"
                coloredLinks
              />
            </Col>
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.photo.test(entry.id): {
        const photos = entry.links();
        const key = `${entry.id}-${index}`;
        if (photos.length >= 1) {
          entries.push(
            <RegularContainer key={key}>
              <Col>
                <ArticlePhoto photos={photos} />
              </Col>
            </RegularContainer>
          );
        } else {
          entries.push(<div key={key} />);
        }
        break;
      }
      case entriesTypes.video.test(entry.id): {
        const youtubeVideoId = entry.metadata('youtube-videoid').value();
        const key = `${entry.id}-${index}`;
        let type = null;
        let videoId = null;
        const addlProps = {};
        if (youtubeVideoId) {
          type = 'EmbeddedYoutube';
          videoId = youtubeVideoId;
          addlProps.creativeId = `video-${youtubeVideoId}`;
          addlProps.videoTitle = 'inline article video';
        }

        if (type && videoId) {
          const videoComponent = (
            <AspectRatioContainer aspectRatio="16:9">
              <Video videoId={videoId} type={type} {...addlProps} />
            </AspectRatioContainer>
          );
          entries.push(
            <RegularContainer key={key}>
              {hasVideo ? (
                <Col>{videoComponent}</Col>
              ) : (
                <ScrollElement id="videos" tag={Col}>
                  {videoComponent}
                </ScrollElement>
              )}
            </RegularContainer>
          );
          hasVideo = true;
        }
        break;
      }
      case entriesTypes.inlineContentAd.test(entry.id): {
        entries.push(
          <RegularContainer key={entry.id}>
            <Col>
              <InlineArticle content={entry.metadata} />
            </Col>
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.adUnitSpot.test(entry.id): {
        // no unique key for ads so index is used
        const key = `ad-block-${index}`;
        entries.push(
          <RegularContainer className="p-0 mb-0" key={key}>
            <Col xs={12} className="mb-1">
              {getAdBlock({ ...entry.options, isMobile })}
            </Col>
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.adUnitMrectRepeatable.test(entry.id): {
        const key = `content-mrect-${index}`;
        entries.push(
          <RegularContainer className="p-0 mb-0" key={key}>
            <Col xs={12}>{getAdBlock({ ...entry.options, isMobile })}</Col>
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.adUnitMrect.test(entry.id): {
        const key = `content-mrect-${index}`;
        entries.push(
          <RegularContainer className="p-0 mb-0" key={key}>
            <Col xs={12}>{entry.component}</Col>
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.adUnitVideo.test(entry.id): {
        entries.push(
          <RegularContainer className="sponsoredvideo p-0 mb-0" key={'sponsoredvideo'}>
            <Col>{entry.component}</Col>
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.adUnitHighImpact.test(entry.id): {
        entries.push(
          <div className="high-impact clearfix mb-2" key={'high-impact'}>
            <div className="me-0 me-md-2 me-lg-0 me-xl-3 p-0">{entry.component}</div>
          </div>
        );
        break;
      }
      case entriesTypes.infoGraphic.test(entry.id): {
        entries.push(
          <RegularContainer key={`${entry.id}-${index.toString()}`}>
            <Col xs={12}>
              <InfographicEntry entry={entry} />
            </Col>
          </RegularContainer>
        );
        break;
      }
      case entriesTypes.shoppingLink.test(entry.id): {
        entries.push(
          <ShoppingLink
            key={`${entry.id}-${index.toString()}`}
            to={entry.metadata('url').value()}
            label={entry.metadata('label').value()}
            description={entry.metadata('description').value()}
            iconClassName={entry.metadata('icon').value()}
            className="rounded px-1 px-md-1_5 py-0_75 py-md-1 bg-white mx-md-1 mb-1"
            descriptionClassName="mt-0_25"
            iconContainerClassName="me-0_75 me-md-1_5 size-16 text-blue-50"
            iconStyles={{ height: '48px', width: '48px' }}
            data-tracking-id="link_select_tool_select"
          />
        );
        break;
      }
      case entriesTypes.evQuiz.test(entry.id): {
        entries.push(<EngineTypeQuiz className="mb-2" />);
        break;
      }
      default:
        break;
    }

    const shouldRenderTrackCarValueModule = isEntriesToRenderLengthGreatThanFour
      ? index === 3
      : entriesToRenderLastIndex === index;
    const shouldRenderAppraisalModule = entriesToRenderLastIndex === index;

    if (includeAppraisalSellModuleInEntries) {
      if (shouldRenderTrackCarValueModule) {
        entries.push(
          <Fragment key="track-car-value-module">
            <hr className="mt-0 mb-2" />
            <MinHeightWrapper componentType="TRACK_CAR_VALUE_MODULE">
              <TrackCarValueModule />
            </MinHeightWrapper>
            <hr className="my-1_5" />
          </Fragment>
        );
      }

      if (shouldRenderAppraisalModule) {
        entries.push(
          <Fragment key="appraiser-sell">
            <hr className="mt-0 mb-2" />
            <MinHeightWrapper componentType="APPRAISER_SELL">
              <AppraiserSellModule className="" isAppraiserTabDefault makeModelEnabled canBeClosed={false} isEmbedded />
            </MinHeightWrapper>
            <hr className="my-1_5" />
          </Fragment>
        );
      }
    }

    if (includeTreehousePromo && !index) {
      entries.push(
        <ArticleTreehousePromotion
          key="treehouse-promotion"
          className="mb-3_5"
          categories={categories}
          slug={slug}
          fallback={<ArticleReadyForEv categories={categories} slug={slug} className="mb-3_5" />}
        />
      );
    }

    return entries;
  }, []);
}
